import { createAction, type QueuedAction } from "@/v2/hooks/useActionQueue";
import { formatEntryId } from "./entryId";
import {
	type ActionDeps,
	ApiError,
	actingHeaders,
	apiCall,
	plural,
	refreshBoth,
} from "./types";

/**
 * Restore soft-deleted entries back to the working set.
 *
 * The v2 PATCH endpoint accepts `deleted → tracked`; restoring to `tracked`
 * keeps the operation unambiguous and lets users review/export afterward.
 */
export function restoreDeletedAction(
	ids: number[],
	deps: ActionDeps,
	timekeeperId?: string | null,
): QueuedAction {
	const wireIds = ids.map(formatEntryId);
	const v2IdSet = new Set(wireIds);
	const rollback =
		deps.updateEntriesV2?.((prev) =>
			prev.map((entry) =>
				v2IdSet.has(entry.id) ? { ...entry, status: "tracked" } : entry,
			),
		) ?? null;
	const n = ids.length;

	return createAction({
		execute: async () => {
			const failures: { id: number; err: unknown }[] = [];
			for (const id of ids) {
				try {
					await apiCall(deps, {
						endpoint: `/v2/entries/${formatEntryId(id)}`,
						method: "PATCH",
						body: { status: "tracked" },
						headers: actingHeaders(timekeeperId),
					});
				} catch (err) {
					failures.push({ id, err });
				}
			}
			if (failures.length > 0) {
				const firstMsg =
					failures[0].err instanceof Error
						? failures[0].err.message
						: "Request failed";
				throw Object.assign(
					new Error(
						`${failures.length} of ${n} ${plural(n)} failed: ${firstMsg}`,
					),
					{ failures },
				);
			}
		},
		rollback,
		toast: {
			loading: `Restoring ${n} ${plural(n)}…`,
			success: `Restored ${n} ${plural(n)}`,
			error: (err) => {
				if (err instanceof ApiError) {
					if (err.code === "ENTRY_EXPORTED")
						return "Exported entries can't be changed";
					if (err.code === "ENTRY_NOT_FOUND")
						return "One or more entries no longer exist";
				}
				return "Failed to restore entries";
			},
		},
		onSuccess: () => refreshBoth(deps),
		onError: () => refreshBoth(deps),
	});
}
